import { useCallback, useEffect, useRef, useState } from 'react'

// ============================================================
// Tipi protocollo
// ============================================================
export type NotifResource =
  | 'task'
  | 'task_status'
  | 'task_project'
  | 'contact'
  | 'appointment'
  | 'lead'
  | 'email'
  | 'document'
  | 'notification'
  | string

export type NotifAction = 'created' | 'updated' | 'deleted' | 'reconnect'

export interface InvalidateMessage {
  type: 'invalidate'
  resource: NotifResource
  action: NotifAction
  id?: number | string
}

type ServerMessage = InvalidateMessage | { type: 'pong' }

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error'

export type Subscriber = (msg: { resource: NotifResource; action: NotifAction; id?: number | string }) => void

// ============================================================
// Helpers
// ============================================================

/**
 * Deriva la URL del WebSocket realtime.
 *
 * Stage/Prod: l'API è su `https://api.{domain}` e Traefik instrada `/ws/*`
 * al servizio api_ws. Quindi WS URL = `wss://api.{domain}/ws/notifications/`.
 *
 * Dev: il container Daphne è esposto direttamente su localhost:8001.
 * WS URL = `ws://localhost:8001/ws/notifications/`.
 *
 * Override esplicito via env: `REACT_APP_NOTIFICATIONS_WS_URL`.
 */
function getNotificationsUrl(): string {
  const explicit = process.env.REACT_APP_NOTIFICATIONS_WS_URL
  if (explicit) return explicit

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api'
  try {
    const url = new URL(apiUrl)
    const isLocal = url.hostname === 'localhost' || url.hostname === '127.0.0.1'
    if (isLocal) {
      return 'ws://localhost:8001/ws/notifications/'
    }
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${wsProtocol}//${url.host}/ws/notifications/`
  } catch {
    return 'ws://localhost:8001/ws/notifications/'
  }
}

// ============================================================
// Hook
// ============================================================
export interface UseNotificationsOptions {
  /** Auto-connect al mount (default: true). Disattivare in pagine non autenticate. */
  autoConnect?: boolean
}

export interface UseNotificationsReturn {
  connectionState: ConnectionState
  isConnected: boolean
  /**
   * Sottoscrive un callback per eventi di una risorsa specifica.
   * Restituisce una funzione di unsubscribe da chiamare nel cleanup di useEffect.
   */
  subscribe: (resource: NotifResource, callback: Subscriber) => () => void
  connect: () => void
  disconnect: () => void
}

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const { autoConnect = true } = options

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const subscribersRef = useRef<Map<NotifResource, Set<Subscriber>>>(new Map())

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected')

  const MAX_RECONNECT_ATTEMPTS = 8
  const RECONNECT_BASE_DELAY = 1000
  const RECONNECT_MAX_DELAY = 30000

  const dispatchToSubscribers = useCallback((msg: InvalidateMessage) => {
    const subs = subscribersRef.current.get(msg.resource)
    if (!subs) return
    subs.forEach((cb) => {
      try {
        cb({ resource: msg.resource, action: msg.action, id: msg.id })
      } catch (err) {
        console.error('[Notifications] subscriber error:', err)
      }
    })
  }, [])

  // Notifica TUTTI i subscriber con action='reconnect' — la pagina rifà
  // un full refetch per recuperare eventi persi durante la disconnessione.
  const notifyReconnect = useCallback(() => {
    subscribersRef.current.forEach((subs, resource) => {
      subs.forEach((cb) => {
        try {
          cb({ resource, action: 'reconnect' })
        } catch (err) {
          console.error('[Notifications] subscriber error on reconnect:', err)
        }
      })
    })
  }, [])

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return
    }
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.onmessage = null
      wsRef.current = null
    }

    const token = localStorage.getItem('accessToken')
    if (!token) {
      // Senza token non possiamo autenticare il WS — restiamo idle.
      setConnectionState('disconnected')
      return
    }

    const url = getNotificationsUrl()
    setConnectionState('connecting')

    try {
      // Sub-protocol "bearer, <jwt>" — il backend lo legge da
      // Sec-WebSocket-Protocol senza che il token finisca nei log Traefik.
      const ws = new WebSocket(url, ['bearer', token])
      wsRef.current = ws

      ws.onopen = () => {
        if (wsRef.current !== ws) return
        const wasReconnect = reconnectAttemptsRef.current > 0
        reconnectAttemptsRef.current = 0
        setConnectionState('connected')
        if (wasReconnect) {
          // Eventi persi mentre eravamo down → invalida tutto, le pagine refetcheranno.
          notifyReconnect()
        }
      }

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return
        try {
          const msg: ServerMessage = JSON.parse(event.data)
          if (msg.type === 'invalidate') {
            dispatchToSubscribers(msg)
          }
        } catch (err) {
          console.error('[Notifications] failed to parse message:', err)
        }
      }

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return
        wsRef.current = null
        setConnectionState('disconnected')

        // Code 4401 = unauthorized dal server: non riconnettere.
        if (event.code === 4401 || event.code === 1000) return

        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(
            RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current),
            RECONNECT_MAX_DELAY,
          )
          reconnectAttemptsRef.current++
          reconnectTimeoutRef.current = setTimeout(connect, delay)
        }
      }

      ws.onerror = () => {
        if (wsRef.current !== ws) return
        setConnectionState('error')
      }
    } catch (err) {
      console.error('[Notifications] failed to create WebSocket:', err)
      setConnectionState('error')
    }
  }, [dispatchToSubscribers, notifyReconnect])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.onmessage = null
      wsRef.current.close(1000, 'Client disconnect')
      wsRef.current = null
    }
    setConnectionState('disconnected')
  }, [])

  const subscribe = useCallback((resource: NotifResource, callback: Subscriber) => {
    let subs = subscribersRef.current.get(resource)
    if (!subs) {
      subs = new Set()
      subscribersRef.current.set(resource, subs)
    }
    subs.add(callback)
    return () => {
      const current = subscribersRef.current.get(resource)
      if (!current) return
      current.delete(callback)
      if (current.size === 0) subscribersRef.current.delete(resource)
    }
  }, [])

  useEffect(() => {
    if (autoConnect) connect()
    return () => disconnect()
  }, [autoConnect, connect, disconnect])

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    subscribe,
    connect,
    disconnect,
  }
}
